import { useCallback, useEffect, useState } from 'react'
import { getSession, logout } from './api/auth'
import { ApiError } from './api/client'
import { AppShell, routeFromHash, type Route } from './components/AppShell'
import { BilibiliLoginDialog } from './components/BilibiliLoginDialog'
import { AiPage } from './pages/AiPage'
import { DataCenterPage } from './pages/DataCenterPage'
import { SelectionPage } from './pages/SelectionPage'
import { StudioPage } from './pages/StudioPage'
import { MemoryPage } from './pages/MemoryPage'
import type { SessionResponse, SessionUser } from './types/api'
import { clearTaskStore } from './ai/taskStore'

export default function App() {
  const [user, setUser] = useState<SessionUser>()
  const [authState, setAuthState] = useState<'loading' | 'login' | 'ready'>('loading')
  const [route, setRoute] = useState<Route>(routeFromHash())

  const refreshSession = useCallback(async () => {
    setAuthState('loading')
    try {
      const session = await getSession()
      if (session.authenticated && session.user) {
        setUser(session.user)
        setAuthState('ready')
      } else {
        setUser(undefined)
        setAuthState('login')
      }
      return session
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status !== 401) console.warn('会话读取失败', error)
      setUser(undefined)
      setAuthState('login')
      return undefined
    }
  }, [])

  useEffect(() => {
    void refreshSession()
    const change = () => setRoute(routeFromHash())
    window.addEventListener('hashchange', change)
    return () => window.removeEventListener('hashchange', change)
  }, [refreshSession])

  const navigate = (next: Route, hash = `#/${next}`) => {
    if (location.hash !== hash) location.hash = hash
    setRoute(next)
  }

  const loginSuccess = useCallback(async () => {
    const session: SessionResponse | undefined = await refreshSession()
    if (session?.authenticated && session.user) navigate('ai')
  }, [refreshSession])

  const onLogout = async () => {
    const currentUserId = user?.id
    try {
      await logout()
    } catch {
      // 即使网络暂时不可用，也清理当前页面状态，避免把旧数据留在公开演示页。
    }
    if (currentUserId) {
      clearTaskStore(currentUserId)
      try {
        localStorage.removeItem(`biliup.quickway.layout.v2.${encodeURIComponent(currentUserId)}`)
      } catch {
        // 浏览器本地存储不是必需能力。
      }
    }
    setUser(undefined)
    setAuthState('login')
  }

  if (authState === 'loading') return <div className="loading-screen"><div className="login-mark">B</div><span>正在连接 Biliup Demo…</span></div>
  if (authState === 'login' || !user) return <main className="login-screen">
    <section className="login-product-panel" aria-label="Biliup 创作助手">
      <div className="login-product-brand"><span className="login-product-logo">B</span><span>Biliup<small>创作助手</small></span></div>
      <div className="login-product-copy">
        <span className="eyebrow">BILIUP CREATOR ASSISTANT</span>
        <h2>从选题开始，<br /><span>把一条视频做完。</span></h2>
        <p>查看账号数据，整理选题，再完成大纲和脚本。</p>
      </div>
      <div className="login-product-preview" aria-hidden="true">
        <div className="login-preview-top"><span className="login-preview-dot" /><span className="login-preview-dot" /><span className="login-preview-dot" /><strong>Biliup 创作助手</strong></div>
        <div className="login-preview-body">
          <div className="login-preview-hero"><span>选题与创作</span><strong>把想法整理成一条视频</strong><i /></div>
          <div className="login-preview-list">
            <div className="login-preview-item active"><i className="ri-radar-line" aria-hidden="true" /><span>灵感选题</span><em>查看</em></div>
            <div className="login-preview-item"><i className="ri-edit-2-line" aria-hidden="true" /><span>创作工坊</span><em>打开</em></div>
            <div className="login-preview-item"><i className="ri-sparkling-2-line" aria-hidden="true" /><span>Biliup AI</span><em>询问</em></div>
          </div>
        </div>
      </div>
    </section>
    <BilibiliLoginDialog onSuccess={loginSuccess} />
  </main>

  const page = route === 'topics'
    ? <SelectionPage />
    : route === 'studio'
      ? <StudioPage />
      : route === 'data'
        ? <DataCenterPage />
        : route === 'memory'
          ? <MemoryPage />
          : <AiPage userId={user.id} />
  return <AppShell user={user} route={route} onNavigate={navigate} onLogout={onLogout}>{page}</AppShell>
}
